/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { mapPaymentMethodDataStatus } from "../../../assets/helper.js";
import { simpleFetch } from "../../../restWrapper.js";
import RenderPaymentCode from "./RenderPaymentCode";
import RenderStripe from "./RenderStripe.js";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import "../PaymentMethodPage.css";

// Import full-page payment method components for reuse
import { RenderAlipay } from "./RenderAlipay.js";
import { RenderWeChatPay } from "./RenderWeChatPay.js";
import { RenderBocPay } from "./RenderBocPay.js";
import { RenderOctopus } from "./RenderOctopus.js";
import RenderFPS from "./RenderFPS.js";
import RenderBankTransfer from "./RenderBankTransfer.js";
import RenderCheckWriting from "./RenderCheckWriting.js";
import RenderCash from "./RenderCash.js";
import RenderPayPalManual from "./RenderPayPalManual.js";
import { RenderTapAndGo } from "./RenderTapAndGo.js";
import { RenderPayPal } from "./RenderPayPal.js";

// Payment method configuration
const PAYMENT_METHOD_CONFIG = {
  stripe: { label: "Stripe", logoClass: "stripe-logo", requiresPlan: null },
  payme: { label: "PayMe", logoClass: "payme-logo", requiresPlan: "pro" },
  paypal: { label: "PayPal", logoClass: "paypal-logo", requiresPlan: "pro" },
  tapandgo: { label: "Tap & Go", logoClass: "tapandgo-logo", requiresPlan: "premium" },
  alipay: { label: "Alipay", logoClass: "alipay-logo", requiresPlan: "premium" },
  wechatpay: { label: "WeChat Pay", logoClass: "wechatpay-logo", requiresPlan: "premium" },
  bocpay: { label: "BoC Pay", logoClass: "bocpay-logo", requiresPlan: "premium" },
  octopus: { label: "Octopus", logoClass: "octopus-logo", requiresPlan: "premium" },
  fps: { label: "FPS", logoClass: "fps-logo", requiresPlan: "premium" },
  bank_transfer: { label: "Bank Transfer", logoClass: "banktransfer-logo", requiresPlan: "premium" },
  check_writing: { label: "Cheque", logoClass: "cheque-logo", requiresPlan: "premium" },
  cash: { label: "Cash", logoClass: "cash-logo", requiresPlan: "premium" },
  paypal_manual: { label: "PayPal", logoClass: "paypal-logo", requiresPlan: "premium" }
};

// Methods that use child component refs for Edit/Save/Cancel
const METHODS_WITH_EDIT_FOOTER = ["fps", "bank_transfer", "check_writing", "cash", "paypal_manual"];

class PaymentMethodPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      formData: this.getInitialFormData(),
      isSaving: false,
      hasChanges: false,
      isEditMode: false,
      saveError: '',
      saveSuccess: false
    };
    this.childComponentRef = React.createRef();
    this.saveSuccessTimer = null;
  }

  componentWillUnmount() {
    if (this.saveSuccessTimer) {
      clearTimeout(this.saveSuccessTimer);
    }
  }

  componentDidUpdate(prevProps) {
    // When organizationObj loads (transitions from empty to populated), update formData
    const prevOrgObj = prevProps.state?.organizationObj || {};
    const currOrgObj = this.props.state?.organizationObj || {};

    // Check if organizationObj has just loaded (clientId appears for PayMe, or other key fields)
    const wasEmpty = !prevOrgObj.clientId && !prevOrgObj.paymentMethodCode;
    const isNowLoaded = currOrgObj.clientId || currOrgObj.paymentMethodCode;

    if (wasEmpty && isNowLoaded) {
      this.setState({
        formData: this.getInitialFormData()
      });
    }

    // Also reload form data if methodKey changes
    if (prevProps.methodKey !== this.props.methodKey) {
      this.setState({
        formData: this.getInitialFormData(),
        hasChanges: false,
        isEditMode: false,
        saveError: '',
        saveSuccess: false
      });
    }
  }

  getInitialFormData() {
    const { methodKey, state } = this.props;
    const orgObj = state.organizationObj || {};
    const paymentMethodCode = orgObj.paymentMethodCode || {};

    switch (methodKey) {
      case "payme":
        return {
          clientId: orgObj.clientId || "",
          clientSecret: orgObj.clientSecret || "",
          signingKeyId: orgObj.signingKeyId || "",
          signingKey: orgObj.signingKey || "",
          paymeTransactionFeePercentage: orgObj.paymeTransactionFeePercentage || "1.5"
        };
      case "bank_transfer":
        return {
          bankName: orgObj.bankName || "",
          bankAccountNumber: orgObj.bankAccountNumber || "",
          bankAccountName: orgObj.bankAccountName || ""
        };
      default:
        return {};
    }
  }

  handleBack = () => {
    // Navigate back to consolidated payment methods page
    this.props.func.clickChangePage(6);
  };

  handleFormChange = (field, value) => {
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [field]: value
      },
      hasChanges: true
    }));
  };

  handleSave = async () => {
    const { methodKey } = this.props;
    this.setState({ isSaving: true, saveError: '', saveSuccess: false });

    try {
      // Call appropriate save endpoint based on method
      let saved = false;
      switch (methodKey) {
        case "payme":
          saved = await this.savePayMe();
          break;
        case "bank_transfer":
          saved = await this.saveBankTransfer();
          break;
        default:
          // For QR code methods, saving is handled differently
          break;
      }
      if (saved) {
        this.setState({ hasChanges: false, saveSuccess: true });
        if (this.saveSuccessTimer) clearTimeout(this.saveSuccessTimer);
        this.saveSuccessTimer = setTimeout(
          () => this.setState({ saveSuccess: false }),
          3000
        );
      }
    } catch (error) {
      // console.error("Error saving:", error);
      this.setState({
        saveError: this.t("saveFailed", "Failed to save. Please try again.")
      });
    } finally {
      this.setState({ isSaving: false });
    }
  };

  // Returns true when the save succeeded; sets saveError otherwise
  handleSaveResponse = (res, refresh) => {
    if (res && res.status === 200) {
      if (refresh) refresh();
      return true;
    }
    const serverMsg = res && res.body && res.body.message;
    this.setState({
      saveError:
        typeof serverMsg === 'string' && serverMsg
          ? serverMsg
          : this.t("saveFailed", "Failed to save. Please try again.")
    });
    return false;
  };

  savePayMe = async () => {
    const { formData } = this.state;
    const { state, func } = this.props;

    const res = await simpleFetch("/user/payments/addPayMe", "POST", {
      username: state.username,
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
      signingKeyId: formData.signingKeyId,
      signingKey: formData.signingKey,
      paymeTransactionFeePercentage: formData.paymeTransactionFeePercentage
    });

    return this.handleSaveResponse(
      res,
      func.loadOrganizationData && (() => func.loadOrganizationData())
    );
  };

  saveBankTransfer = async () => {
    const { formData } = this.state;
    const { func } = this.props;

    const res = await simpleFetch("/user/payments/addBankTransfer", "POST", {
      bankName: formData.bankName,
      bankAccountNumber: formData.bankAccountNumber,
      bankAccountName: formData.bankAccountName
    });

    return this.handleSaveResponse(
      res,
      func.getOrgData && (() => func.getOrgData())
    );
  };

  // Handlers for child component edit state (FPS, Bank Transfer, Cheque, Cash)
  handleEdit = () => {
    if (this.childComponentRef.current?.handleToggleEditMode) {
      this.childComponentRef.current.handleToggleEditMode();
    }
    this.setState({ isEditMode: true });
  };

  handleChildSave = () => {
    if (this.childComponentRef.current?.handleSave) {
      this.childComponentRef.current.handleSave();
    }
    this.setState({ isEditMode: false });
  };

  handleCancel = () => {
    if (this.childComponentRef.current?.handleToggleEditMode) {
      this.childComponentRef.current.handleToggleEditMode();
    }
    this.setState({ isEditMode: false });
  };

  t(key, fallback) {
    const pmText = (this.props.languageObj && this.props.languageObj.PaymentMethods) || {};
    return pmText[key] || fallback;
  }

  renderContent() {
    const { methodKey, state, func, languageObj } = this.props;

    switch (methodKey) {
      case "stripe":
        return this.renderStripeContent();
      case "payme":
        return this.renderPayMeContent();
      case "fps":
        return <RenderFPS ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;
      case "bank_transfer":
        return <RenderBankTransfer ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;
      case "alipay":
        return RenderAlipay(state, func, languageObj);
      case "wechatpay":
        return RenderWeChatPay(state, func, languageObj);
      case "bocpay":
        return RenderBocPay(state, func, languageObj);
      case "octopus":
        return RenderOctopus(state, func, languageObj);
      case "tapandgo":
        return RenderTapAndGo(state, func, languageObj);
      case "check_writing":
        return <RenderCheckWriting ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;
      case "cash":
        return <RenderCash ref={this.childComponentRef} state={state} func={func} languageObj={languageObj} />;
      case "paypal_manual":
        return <RenderPayPalManual ref={this.childComponentRef} state={state} func={func} />;
      case "paypal":
        return RenderPayPal(state, func, languageObj);
      default:
        return (
          <div className="PaymentMethodPage-notImplemented">
            <p>{this.t("notAvailable", "Configuration for this payment method is not yet available.")}</p>
          </div>
        );
    }
  }

  renderStripeContent() {
    const { state, func, languageObj } = this.props;

    return (
      <RenderStripe
        languageObj={languageObj?.Stripe || {}}
        orgID={Number(state.orgID)}
        bankAc={state.bankAc}
        enableStripeLink={state.organizationObj?.enableStripeLink !== false}
        onToggleEnableStripeLink={func.handleToggleEnableStripeLink}
        loading={state.loading}
      />
    );
  }

  renderPayMeContent() {
    const { formData } = this.state;

    return (
      <div className="PaymentMethodPage-formContent">
        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("clientId", "Client ID")}</label>
          <input
            type="text"
            className="PaymentMethodPage-input"
            value={formData.clientId || ""}
            onChange={(e) => this.handleFormChange("clientId", e.target.value)}
            placeholder={this.t("clientId", "Client ID")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("clientSecret", "Client Secret")}</label>
          <input
            type="password"
            className="PaymentMethodPage-input"
            value={formData.clientSecret || ""}
            onChange={(e) => this.handleFormChange("clientSecret", e.target.value)}
            placeholder={this.t("clientSecret", "Client Secret")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("signingKeyId", "Signing Key ID")}</label>
          <input
            type="text"
            className="PaymentMethodPage-input"
            value={formData.signingKeyId || ""}
            onChange={(e) => this.handleFormChange("signingKeyId", e.target.value)}
            placeholder={this.t("signingKeyId", "Signing Key ID")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("signingKey", "Signing Key")}</label>
          <input
            type="password"
            className="PaymentMethodPage-input"
            value={formData.signingKey || ""}
            onChange={(e) => this.handleFormChange("signingKey", e.target.value)}
            placeholder={this.t("signingKey", "Signing Key")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("transactionFee", "Transaction Fee Percentage")}</label>
          <input
            type="number"
            className="PaymentMethodPage-input"
            value={formData.paymeTransactionFeePercentage || ""}
            onChange={(e) => this.handleFormChange("paymeTransactionFeePercentage", e.target.value)}
            placeholder="1.5"
            step="0.1"
            min="0"
          />
        </div>
      </div>
    );
  }

  renderBankTransferContent() {
    const { formData } = this.state;

    return (
      <div className="PaymentMethodPage-formContent">
        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("bankName", "Bank Name")}</label>
          <input
            type="text"
            className="PaymentMethodPage-input"
            value={formData.bankName || ""}
            onChange={(e) => this.handleFormChange("bankName", e.target.value)}
            placeholder={this.t("bankName", "Bank Name")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("accountNumber", "Account Number")}</label>
          <input
            type="text"
            className="PaymentMethodPage-input"
            value={formData.bankAccountNumber || ""}
            onChange={(e) => this.handleFormChange("bankAccountNumber", e.target.value)}
            placeholder={this.t("accountNumber", "Account Number")}
          />
        </div>

        <div className="PaymentMethodPage-formGroup">
          <label className="PaymentMethodPage-label">{this.t("accountName", "Account Name")}</label>
          <input
            type="text"
            className="PaymentMethodPage-input"
            value={formData.bankAccountName || ""}
            onChange={(e) => this.handleFormChange("bankAccountName", e.target.value)}
            placeholder={this.t("accountName", "Account Name")}
          />
        </div>
      </div>
    );
  }

  renderQRCodeContent(variants) {
    const { state, func } = this.props;
    const orgObj = state.organizationObj || {};
    const paymentMethodCode = orgObj.paymentMethodCode || {};

    return (
      <div className="PaymentMethodPage-qrContent">
        {variants.map((variant) => {
          const status = mapPaymentMethodDataStatus(paymentMethodCode, variant.key);
          return (
            <div key={variant.key} className="PaymentMethodPage-qrVariant">
              <div className="PaymentMethodPage-qrHeader">
                <span className="PaymentMethodPage-qrLabel">{variant.label}</span>
                {status.show && (
                  <ToggleSwitch
                    checked={status.checked}
                    onChange={(e) => {
                      if (func.handleUpdatePaymentStatus) {
                        func.handleUpdatePaymentStatus(e, variant.key);
                      }
                    }}
                  />
                )}
              </div>
              <RenderPaymentCode
                state={state}
                func={func}
                paymentMethod={variant}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderSimpleToggleContent() {
    const { methodKey, state, func } = this.props;
    const orgObj = state.organizationObj || {};
    const paymentMethodCode = orgObj.paymentMethodCode || {};
    const status = mapPaymentMethodDataStatus(paymentMethodCode, methodKey);
    const config = PAYMENT_METHOD_CONFIG[methodKey];

    return (
      <div className="PaymentMethodPage-simpleToggle">
        <div className="PaymentMethodPage-toggleRow">
          <span className="PaymentMethodPage-toggleLabel">
            Enable {config?.label || methodKey}
          </span>
          {status.show && (
            <ToggleSwitch
              checked={status.checked}
              onChange={(e) => {
                if (func.handleUpdatePaymentStatus) {
                  func.handleUpdatePaymentStatus(e, methodKey);
                }
              }}
            />
          )}
        </div>
        <p className="PaymentMethodPage-toggleDescription">
          {status.show
            ? this.t("enableDesc", "When enabled, this payment method will be available to donors.")
            : this.t("uploadDesc", "Upload a QR code or configure this payment method to enable it.")}
        </p>
      </div>
    );
  }

  render() {
    const { methodKey } = this.props;
    const { isSaving, hasChanges, isEditMode, saveError, saveSuccess } = this.state;
    const config = PAYMENT_METHOD_CONFIG[methodKey];
    const showPayMeFooter = methodKey === "payme";
    const showEditFooter = METHODS_WITH_EDIT_FOOTER.includes(methodKey);

    return (
      <div className="PaymentMethodPage-container">
        <div className="PaymentMethodPage-header">
          <button className="PaymentMethodPage-backBtn" onClick={this.handleBack}>
            <span className="PaymentMethodPage-backIcon"></span>
            {this.t("back", "Back")}
          </button>
          <h2 className="PaymentMethodPage-title">
            <span className={`PaymentMethodPage-logo ${config?.logoClass || ""}`}></span>
            {config?.label || methodKey}
          </h2>
        </div>

        <div className="PaymentMethodPage-content PaymentMethodPage-hideComponentHeader">
          {this.renderContent()}
        </div>

        {/* PayMe footer - always editable */}
        {showPayMeFooter && (
          <div className="PaymentMethodPage-footer">
            {saveError && (
              <span className="PaymentMethodPage-saveError" role="alert">
                {saveError}
              </span>
            )}
            {saveSuccess && (
              <span className="PaymentMethodPage-saveSuccess">
                {this.t("saved", "Saved")}
              </span>
            )}
            <button
              className="PaymentMethodPage-cancelBtn"
              onClick={this.handleBack}
              disabled={isSaving}
            >
              {this.t("cancel", "Cancel")}
            </button>
            <button
              className="PaymentMethodPage-saveBtn"
              onClick={this.handleSave}
              disabled={isSaving || !hasChanges}
            >
              {isSaving ? this.t("saving", "Saving...") : this.t("saveChanges", "Save Changes")}
            </button>
          </div>
        )}

        {/* Edit footer for FPS, Bank Transfer, Cheque, Cash */}
        {showEditFooter && (
          <div className="PaymentMethodPage-footer">
            {isEditMode ? (
              <>
                <button
                  className="PaymentMethodPage-cancelBtn"
                  onClick={this.handleCancel}
                >
                  {this.t("cancel", "Cancel")}
                </button>
                <button
                  className="PaymentMethodPage-saveBtn"
                  onClick={this.handleChildSave}
                >
                  {this.t("save", "Save")}
                </button>
              </>
            ) : (
              <button
                className="PaymentMethodPage-saveBtn"
                onClick={this.handleEdit}
              >
                {this.t("edit", "Edit")}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
}

PaymentMethodPage.propTypes = {
  methodKey: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object
};

export default PaymentMethodPage;
